// Pure plain-text<->HTML round-trip helpers for RichReplyEditor.jsx —
// extracted so this (the actual bug-prone logic) is unit-testable with
// node:test without any React/TipTap machinery, same convention as
// templateMerge.js/suggestionActionInputs.js.
//
// draftReply/humanReply keep flowing through redaction/restore, the
// [AGENT_FIRST_NAME] signature-token resolution, and the send path as plain
// JS strings exactly as before (see pipeline.js/send-approved-reply.js) —
// RichReplyEditor does not change that contract. What it DOES change is what
// that string can now legitimately CONTAIN: real HTML, when the draft
// actually has rich content (an inserted "1A Website" template's bold/links —
// see import-1a-website-templates.js), so a CSR can see and edit that
// formatting instead of raw tags or flattened plain text.
//
// The critical design point, and the reason this needs care: every EXISTING
// draft (every eBay draft, every M365 draft that never touched a rich
// template) is plain text today, with nothing downstream expecting it to
// gain HTML markup. If the editor always serialized its content as HTML
// (TipTap's native format), a perfectly plain draft would come back out as
// "<p>plain text</p>" instead of "plain text" — a silent, permanent change
// to the stored value's SHAPE for every channel, not just 1A Website, with
// no send-path change permitted to compensate (out of scope, see the task
// note). So the round-trip is deliberately asymmetric:
//   - LOADING: a plain string is turned into the minimal HTML needed to
//     display it (paragraphs from blank-line breaks, <br> from single
//     newlines — the same convention templatePreview.js's previewHtml()
//     already uses for plain-text templates) — real HTML (looksLikeHtml,
//     same regex previewHtml() uses) loads as-is.
//   - SAVING: if the edited content, once you strip out <p>/<br> alone,
//     contains no other tag (no bold, no link, no anything else the CSR or
//     an inserted template actually added), it is flattened BACK down to
//     the exact plain string it would have been as a plain textarea — byte-
//     identical shape to today for the common case. Only genuinely
//     formatted content (a real <strong>/<a>/etc. survives the strip) is
//     kept as real HTML.
// This means eBay/every plain-text flow sees zero behavior change, and HTML
// only ever enters draftReply/humanReply when something real earned it.
const TRIVIAL_TAG_RE = /<\/?(?:p|br)[^>]*>/gi

export function hasRichFormatting(html) {
  return /<[a-z]/i.test((html ?? '').replace(TRIVIAL_TAG_RE, ''))
}

function escapeHtml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

export function plainTextToEditableHtml(text) {
  if (!text) return '<p></p>'
  return text
    .split(/\n{2,}/)
    .map((para) => `<p>${escapeHtml(para).replace(/\n/g, '<br>')}</p>`)
    .join('')
}

function htmlToPlainTextFlat(html) {
  // Matches ANY <br>, including a data-tight one (TemplateRichEditor.jsx's
  // TightHardBreak) — both already mean exactly the same thing here, a
  // single \n, no marker needed: this is the LOAD-side symmetric inverse of
  // plainTextToEditableHtml() below, whose own single-\n -> <br> conversion
  // never distinguishes tight from bare either.
  return html
    .replace(/<br[^>]*>/gi, '\n')
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
    .replace(/^<p[^>]*>/i, '')
    .replace(/<\/p>\s*$/i, '')
}

// Same detection templatePreview.js's previewHtml() uses — a value already
// containing a real tag is treated as HTML we own the shape of; anything
// else is plain text this module is responsible for wrapping to display.
export function looksLikeHtml(value) {
  return /<[a-z][\s\S]*>/i.test(value ?? '')
}

export function toEditableHtml(value) {
  if (!value) return '<p></p>'
  return looksLikeHtml(value) ? value : plainTextToEditableHtml(value)
}

export function fromEditorHtml(html) {
  return hasRichFormatting(html) ? html : htmlToPlainTextFlat(html)
}

// Convert HTML template/rich-draft content to plain text suitable for a
// plain-text display or field — e.g. a template picked from the "1A
// Website" library, or an M365 rich-editor draft/sent-reply shown in the
// Activity Log. Decodes HTML entities via a textarea round-trip (browser-
// only — this function is not node:test-callable, unlike the rest of this
// module), converts block-level elements to blank-line breaks, strips
// remaining tags, then normalizes whitespace.
// A data-tight <br> (TemplateRichEditor.jsx's TightHardBreak) must survive
// as a single \n, distinct from every other break — which all still
// collapse to exactly one blank line (\n\n), the 2026-07-29 consistency fix
// (PR #467). Marked with this private-use-area placeholder rather than a
// literal \n so it passes through the universal "\n -> \n\n" doubling below
// (and the second textarea round-trip, which would otherwise mangle a
// literal NULL/control character) completely untouched, then swapped back
// to a real single \n only at the very end.
const TIGHT_BREAK_PLACEHOLDER = String.fromCharCode(0xE000)

export function htmlToPlainText(html) {
  const textarea = document.createElement('textarea')
  textarea.innerHTML = html
  let text = textarea.value

  // Order matters — the tight-marker replacement must run before the bare-
  // <br> one, since a marked tag also matches the bare pattern.
  text = text
    .replace(/<br[^>]*\bdata-tight\b[^>]*>/gi, TIGHT_BREAK_PLACEHOLDER)
    .replace(/<br\s*\/?>/gi, '\n\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/div>/gi, '\n\n')
    .replace(/<\/li>/gi, '\n\n')
    .replace(/<\/h[1-6]>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')

  textarea.innerHTML = text
  text = textarea.value

  return text
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n +| +\n/g, '\n')
    .replace(/\n/g, '\n\n') // double every remaining single break, including bare \n that was already literal in the source
    .replace(/\n{3,}/g, '\n\n')
    .replaceAll(TIGHT_BREAK_PLACEHOLDER, '\n')
    .trim()
}

// Substitutions for real HTML template content (1A Website templates — see
// import-1a-website-templates.js) inserted directly into the rich draft
// editor rather than flattened to plain text first (SuggestionPanel.jsx's
// applySubstitutions handles the plain-text path for every other template
// folder). $(user.firstName)/<INSERT TRACKING LINK> don't apply here — Phase
// 1 import already converts the former to [AGENT_FIRST_NAME] (resolved
// separately, the same way eBay's footer token is) and this library has no
// tracking-link placeholder convention. The one substitution that DOES still
// apply — inserting erpOrderId after "Your Reference ID #:" — has to
// tolerate that label living inside a <strong> (the real source shape,
// confirmed by import-1a-website-templates.js's own dry-run), which a plain
// end-of-line text regex can't see through.
// Apply template substitutions:
//  - $(user.firstName) → agent's first name
//  - Your Reference ID #: → append erpOrderId on next line
//  - <INSERT TRACKING LINK> → replace with trackingUrl if available
export function applySubstitutions(text, { firstName, erpOrderId, trackingUrl }) {
  if (firstName) {
    text = text.replace(/\$\(user\.firstName\)/g, firstName)
  }

  if (erpOrderId) {
    text = text.replace(
      /(Your Reference ID #:\s*)(\n|$)/,
      `$1\n${erpOrderId}\n`
    )
  }

  if (trackingUrl) {
    text = text.replace(/<INSERT TRACKING LINK>/gi, trackingUrl)
  }

  return text
}

export function applySubstitutionsHtml(html, { erpOrderId } = {}) {
  if (!erpOrderId) return html
  return html.replace(
    /(Your Reference ID #:)(<\/(?:strong|b)>)?/i,
    (_m, label, closeTag) => `${label}${closeTag ?? ''}<br>${erpOrderId}`
  )
}

// --- m365_email inline-image/attachment size cap ---------------------------
// Combined size cap for a single m365_email reply's inline images + file
// attachments — Microsoft Graph's practical limit for a single direct
// sendMail/reply call before requiring a chunked upload session (out of
// scope here). Shared by RichReplyEditor.jsx (inline images) and
// SuggestionPanel.jsx (file attachments), so both draw from the same running
// total rather than each enforcing an independent, smaller effective cap.
// Enforced here client-side for immediate feedback; send-approved-reply.js
// enforces it again server-side, since client-side validation alone is
// never trusted.
export const MAX_EMAIL_ATTACHMENT_BYTES = 3 * 1024 * 1024

// The raw byte size a base64 payload decodes to — what's actually
// transmitted, not the base64 string's own (33% larger) length.
export function estimateBase64Bytes(base64) {
  return base64 ? Math.ceil(base64.length * 3 / 4) : 0
}

function estimateDataUrlBytes(dataUrl) {
  const match = /^data:[^;]*;base64,(.*)$/is.exec(dataUrl ?? '')
  return match ? estimateBase64Bytes(match[1]) : 0
}

// Sums every inline <img src="data:...;base64,...."> tag's decoded size —
// the running total a new inline image insertion (RichReplyEditor.jsx) or a
// new file attachment (SuggestionPanel.jsx) is checked against, alongside
// any separately-tracked file attachments.
export function estimateInlineImageBytes(html) {
  if (!html) return 0
  let total = 0
  const re = /<img[^>]*\ssrc="(data:[^"]*)"[^>]*>/gi
  let m
  while ((m = re.exec(html))) {
    total += estimateDataUrlBytes(m[1])
  }
  return total
}
